import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { map, takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { AccelaControl, AccelaControlType } from 'src/app/modules/shared/model/accelaControl.model';
import { LoginSettingsResponse } from 'src/app/modules/shared/model/loginSettingsResponse.model';
import { UserModel } from 'src/app/modules/shared/model/userModel.model';
import { selectLoggedInUser } from 'src/app/state/selectors/loggedin-user.selectors';
import { OptionKeyValidator } from 'src/app/validators/optionkeymatch.validator';
import { ApiProviderService } from 'src/app/services/api-provider.service';
import { selectAppSettings } from 'src/app/state/selectors/app-settings.selectors';
import { Subject } from 'rxjs';
import { LocalizationService } from 'src/app/modules/shared/services/localization.service';
import { UserManagementConst } from '../../model/user-management.const.model';
import { isStringNullOrWhiteSpace } from 'src/app/util/StringUtil';
import { AccountService } from 'src/app/modules/shared/services/account.service';
import { EncryptionService } from 'src/app/modules/shared/services/encryption.service';
import { NavigationService } from 'src/app/modules/shared/services/navigation-service.service';
import { SettingService } from 'src/app/modules/shared/services/setting.service';

@Component({
  selector: 'aca-security-question-auth-screen',
  templateUrl: './security-question-auth-screen.component.html',
  styleUrls: ['./security-question-auth-screen.component.css']
})
export class SecurityQuestionAuthScreenComponent implements OnInit, OnDestroy {

  QuestionForm: UntypedFormGroup;
  SectionKey = 'Answer Security Questions';
  UserCredentials: UserModel;
  securityQuestionsSettings: LoginSettingsResponse;
  securityQuestions: Array<AccelaControl> = new Array<AccelaControl>();
  showRegistrationLinks = false;
  adminMode: boolean;
  returnURL = '';
  module = '';
  viewId = '';
  licRequired = false;

  protected destroyActions = new Subject<boolean>();

  constructor(private store: Store<AppState>, private formBuilder: UntypedFormBuilder, private messageService: MessageService,
    private activatedRoute: ActivatedRoute, private apiRef: ApiProviderService, private accountService: AccountService,
    public navigationService: NavigationService, private localizationService: LocalizationService, public settingService: SettingService,
    private encryptionService: EncryptionService) {
    this.QuestionForm = this.formBuilder.group({});
  }

  ngOnInit(): void {
    this.store.select(selectAppSettings)
      .pipe(takeUntil(this.destroyActions))
      .subscribe({
        next: state => {
          this.adminMode = state.AdminMode;
        }
      });

    this.settingService.isRegistrationEnabled().subscribe(r => this.showRegistrationLinks = r);
    if (!this.adminMode) {
      this.store.select(selectLoggedInUser)
      .pipe(takeUntil(this.destroyActions))
      .subscribe((resp => {
        if (resp && (resp as any).LoggedInUser) {
          this.UserCredentials = (resp as any).LoggedInUser;
        } else {
          let userData = decodeURI(this.activatedRoute.snapshot.queryParamMap.get('userData'));
          if (userData !== 'null') {
            userData = userData.split(' ').join('+'); // acts as a replace all
            this.UserCredentials = this.encryptionService.decryptObjRabbit(userData);
          } else {
            const msg = $localize`Error: Unable to load data about user attempting login.`;
            this.messageService.add({
              severity: 'error',
              summary: msg,
              closable: false
            });
          }
        }

        this.accountService.getSecurityQuestions(this.UserCredentials?.Name)
          .pipe(takeUntil(this.destroyActions))
          .subscribe(res => {
            let idx = 0;
            res.forEach(question => {
              idx++;
              const control = new AccelaControl();
              control.name = question.Key + idx.toString();
              control.label = question.Key;
              control.tooltip = '';
              control.type = AccelaControlType.SecurityAnswer;
              control.optionKey = question.Value;
              control.value = '';
              control.required = true;
              control.placeholder = '';
              this.securityQuestions.push(control);

              const abstractControl = new UntypedFormControl(control);
              abstractControl.setValue('');
              abstractControl.controlData = control;
              abstractControl.setValidators([Validators.required, OptionKeyValidator()]);
              this.QuestionForm.addControl(question.Key + idx.toString(), abstractControl);
            });
          });

        return resp;
      })
        , error => {
          this.messageService.add({
            severity: 'error',
            summary: error,
            closable: false
          });
        });
    }
    this.localizationService.getLabelByKey('aca_securityquestion_verification_label_title')
      .pipe(takeUntil(this.destroyActions))
      .subscribe((resp => {
      this.SectionKey = resp;
    })); // aca_securityquestion_verification_label_title

    const urlParams = new URLSearchParams(window.location.search);

    //check for and store any ReturnUrl parameters
    const rUrlParam =  urlParams.get(UserManagementConst.RETURN_URL)?.trim();
    if(!isStringNullOrWhiteSpace(rUrlParam)){
      const url = this.apiRef.nativeWindow.location.search;
      this.returnURL = decodeURIComponent(url.substring(url.indexOf(UserManagementConst.RETURN_URL)+10));
    }

    //check for and store any module parameters
    const moduleParam = urlParams.get('module')?.trim();
    if(!isStringNullOrWhiteSpace(moduleParam)){
      this.module = moduleParam;
    }
    this.settingService.isLicenseRequiredForRegistration()
      .pipe(takeUntil(this.destroyActions))
      .subscribe(res => {
        if (res) {
          this.licRequired = res;
        }
      });
  }


  getLabelId(idx: number) {
    return 'label' + idx.toString();
  }

  validateForm(): boolean {
    let valid = true;
    if(Object.keys(this.QuestionForm.controls).length === 0){
      return false;
    }

    for (const simpleElemName in this.QuestionForm.controls) {
        if (Object.prototype.hasOwnProperty.call(this.QuestionForm.controls, simpleElemName)) {
        const formControl = this.QuestionForm.controls[simpleElemName] as UntypedFormControl;
        if(formControl.validator){
          formControl.markAsDirty();
        }
        if (formControl.errors && Object.keys(formControl.errors).length > 0) {
          valid = false;
        }
      }
    }
    return valid;
  }

  submitQuestions(data: Event) {
    data.preventDefault();

    if (this.adminMode) {
      return;
    }
    this.QuestionForm.updateValueAndValidity();
    const valid = this.validateForm();

    if (!valid) {
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: $localize`Not all fields are filled out correctly. Please check again.`,
        closable: false
      });
      return;
    }
    this.accountService.loginToACA(this.UserCredentials).subscribe((resp => {
      if(resp.forcePasswordChange){
        resp.url = UserManagementConst.CHANGE_PASSWORD_ROUTE + `&userID=${this.UserCredentials.Name}`;
      }

      if (resp.type === 'success') {
        if (!isStringNullOrWhiteSpace(this.returnURL)) {
          const idx = this.returnURL.indexOf('?');
          let url = `${this.returnURL}${(idx < 0 ? '?' : '&')}module=${this.module??''}`;
          if(this.activatedRoute.snapshot.queryParamMap.get('Embed')){
            url += `&Embed=true`;
          }
          if(this.activatedRoute.snapshot.queryParamMap.get('OpenCities')){
            url += `&OpenCities=true`;
          }
          this.apiRef.nativeWindow.location.href = url;
        } else {
          if (this.activatedRoute.snapshot.queryParamMap.get('Embed')) {
            const idx = resp.url.indexOf('?');
            let url = this.apiRef.vDir + resp.url + ((idx < 0) ? '?' : '&') + 'Embed=true';
            if(this.activatedRoute.snapshot.queryParamMap.get('OpenCities')){
              url += `&OpenCities=true`;
            }
            this.apiRef.nativeWindow.location.href = url;
          } else {
            this.apiRef.nativeWindow.location.href = this.apiRef.vDir + resp.url;
          }

        }
      } else if (resp.type === 'redirect') {
        this.apiRef.nativeWindow.location.href = this.apiRef.vDir + resp.url;
      } else if (resp.type === 'error') {
        this.messageService.add({
          severity: 'error',
          summary: resp.message,
          closable: false
        });
        this.navigationService.navigateRoute('login');
      }
    })
    , error => {
      this.messageService.add({
        severity: 'error',
        summary: error,
        closable: false
      });
    });
  }

  navigateToSignUp(): void {
    if (!this.adminMode) {
      if (this.licRequired) {
        this.navigationService.navigateRoute('account/register-declare-license');
      } else {
        this.navigationService.navigateRoute('account/new');
      }
    }
  }


  ngOnDestroy(): void {
    this.destroyActions.next(null);
    this.destroyActions.complete();
  }
}
